import { inject, Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormArray,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  IFooterBlockForm,
  IFooterForm,
  IFooterFormData,
  IFooterLayoutForm,
} from '../../entities/footer-entity';
import { Factory } from '../factory';
import { ImageFormsFactoryService } from '../image/image-forms-factory.service';
import { LinkFormsFactoryService } from '../link/link-forms-factory.service';
import { ILogoAreaForm } from '../../entities/logo-area-entity';
import { IAddressAreaForm } from '../../entities/address-area-entity';
import { IPaymentAreaForm } from '../../entities/payment-area-entity';
import { ILinkAreaForm } from '../../entities/link-area-entity';
import { FilterTextsPipe } from '../../pipes/filterTexts/filterTexts.pipe';
import { FOOTER_LAYOUT_ROW_BREAK } from '../../components/data-input/drag-drop-grid/drag-drop-grid';
import {
  DEFAULT_FOOTER,
  DEFAULT_FOOTER_AREA_TYPE,
  IAddressArea,
  IFooter,
  IText,
  IFooterBlock,
  FooterBlockId,
  EFooterBlockType,
  ERelationType,
  toFooterBlockType,
} from '@web/dtos';

@Injectable({
  providedIn: 'root',
})
export class FooterFormsFactoryService extends Factory<IFooterForm, IFooter> {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly imageFormsFactory: ImageFormsFactoryService = inject(
    ImageFormsFactoryService
  );
  private readonly linkFormsFactory: LinkFormsFactoryService = inject(
    LinkFormsFactoryService
  );
  private readonly filterTexts: FilterTextsPipe = inject(FilterTextsPipe);

  buildForm(entity: IFooter = DEFAULT_FOOTER): FormGroup<IFooterForm> {
    const blocks = (entity.blocks ?? [])
      .filter(block => block.positionOrder >= 4)
      .slice(0, 3);
    const defaultBlockOrder = [
      'logoArea',
      'addressArea',
      'paymentArea',
      'linkArea',
    ];
    const customBlockIds = blocks
      .map(block => block.id)
      .filter((blockId): blockId is NonNullable<typeof blockId> => !!blockId);
    const validBlockIds = new Set([
      FOOTER_LAYOUT_ROW_BREAK,
      ...defaultBlockOrder,
      ...customBlockIds,
    ]);
    const blockOrder = [
      ...(entity.layout?.blockOrder ?? defaultBlockOrder).filter(blockId =>
        validBlockIds.has(blockId)
      ),
      ...customBlockIds.filter(
        blockId =>
          !(entity.layout?.blockOrder ?? defaultBlockOrder).includes(blockId)
      ),
    ];
    const hiddenBlocks = entity.layout?.hiddenBlocks ?? [];

    return this.formBuilder.group<IFooterForm>({
      id: this.formBuilder.control(entity.id || null),

      backgroundColor: this.formBuilder.nonNullable.control(
        entity.backgroundColor,
        [Validators.required]
      ),

      appearance: this.formBuilder.group({
        showBackgroundImage: this.formBuilder.nonNullable.control(
          entity.appearance?.showBackgroundImage ?? false
        ),
        backgroundImage: this.formBuilder.control(
          entity.appearance?.backgroundImage ?? null
        ),
        backgroundImageAlt: this.formBuilder.control(
          entity.appearance?.backgroundImageAlt ?? null
        ),
        fontColor: this.formBuilder.nonNullable.control(
          entity.appearance?.fontColor ?? '#000000'
        ),
        backgroundColor: this.formBuilder.nonNullable.control(
          entity.appearance?.backgroundColor ?? '#FFFFFF'
        ),
        fontColorDark: this.formBuilder.nonNullable.control(
          entity.appearance?.fontColorDark ?? '#FFFFFF'
        ),
        backgroundColorDark: this.formBuilder.nonNullable.control(
          entity.appearance?.backgroundColorDark ?? '#121212'
        ),
      }),

      layout: this.formBuilder.group<IFooterLayoutForm>({
        blockOrder: new FormArray<FormControl<string>>(
          blockOrder.map(blockId =>
            this.formBuilder.nonNullable.control(blockId)
          )
        ),
        hiddenBlocks: new FormArray<FormControl<string>>(
          hiddenBlocks.map(blockId =>
            this.formBuilder.nonNullable.control(blockId)
          )
        ),
      }),
      blocks: this.formBuilder.array(
        blocks.map((block, index) => this.buildBlockForm(block, index))
      ),
      logoArea: this.formBuilder.group<ILogoAreaForm>({
        id: this.formBuilder.control(entity.logoArea.id || null),
        isActive: this.formBuilder.nonNullable.control(
          entity.logoArea.isActive,
          [Validators.required]
        ),
        headlines: this.formBuilder.nonNullable.control(
          entity.logoArea.headlines
        ),
        accessibleImages:
          entity.logoArea.accessibleImages.length > 0
            ? this.imageFormsFactory.buildFormArray(
                entity.logoArea.accessibleImages
              )
            : this.formBuilder.array([
                this.imageFormsFactory.buildImageFormGroup(undefined, 0),
              ]),
        type: this.formBuilder.nonNullable.control(
          toFooterBlockType(
            entity.logoArea.type,
            DEFAULT_FOOTER_AREA_TYPE.logoArea
          )
        ),
        orientation: this.formBuilder.nonNullable.control(
          (entity.logoArea as any)?.orientation ?? 'vertical'
        ),
      }),
      addressArea: this.formBuilder.group<IAddressAreaForm>({
        id: this.formBuilder.control(entity.addressArea.id || null),
        isActive: this.formBuilder.nonNullable.control(
          entity.addressArea.isActive,
          [Validators.required]
        ),
        headlines: this.formBuilder.nonNullable.control(
          this.filterTexts.transform(
            entity.addressArea.texts,
            ERelationType.headline
          )
        ),
        texts: this.formBuilder.nonNullable.control(
          this.filterTexts.transform(
            entity.addressArea.texts,
            ERelationType.text
          )
        ),
        type: this.formBuilder.nonNullable.control(
          toFooterBlockType(
            entity.addressArea.type,
            DEFAULT_FOOTER_AREA_TYPE.addressArea
          )
        ),
      }),
      paymentArea: this.formBuilder.group<IPaymentAreaForm>({
        id: this.formBuilder.control(entity.paymentArea.id || null),
        isActive: this.formBuilder.nonNullable.control(
          entity.paymentArea.isActive,
          [Validators.required]
        ),
        headlines: this.formBuilder.nonNullable.control(
          entity.paymentArea.headlines
        ),
        accessibleImages: this.imageFormsFactory.buildFormArray(
          entity.paymentArea.accessibleImages
        ),
        type: this.formBuilder.nonNullable.control(
          toFooterBlockType(
            entity.paymentArea.type,
            DEFAULT_FOOTER_AREA_TYPE.paymentArea
          )
        ),
      }),
      linkArea: this.formBuilder.group<ILinkAreaForm>({
        id: this.formBuilder.control(entity.linkArea.id || null),
        headlines: this.formBuilder.nonNullable.control(
          entity.linkArea.headlines
        ),
        links: this.linkFormsFactory.buildFormArray(entity.linkArea.links),
        type: this.formBuilder.nonNullable.control(
          toFooterBlockType(
            entity.linkArea.type,
            DEFAULT_FOOTER_AREA_TYPE.linkArea
          )
        ),
      }),
    });
  }

  buildEntity(form: FormGroup<IFooterForm>): IFooter {
    const footer = form.getRawValue() as unknown as IFooterFormData;
    const hiddenBlocks: string[] =
      form.controls.layout.controls.hiddenBlocks.getRawValue() ?? [];
    const blocks = form.controls.blocks.getRawValue();

    const newAddressArea: IAddressArea = {
      id: footer.addressArea.id,
      isActive: footer.addressArea.isActive,
      texts: [
        ...(footer.addressArea.texts ?? []),
        ...(footer.addressArea.headlines ?? []),
      ],
    };

    const newFooter: any = {
      id: footer.id,
      backgroundColor: footer.backgroundColor,
      appearance: footer.appearance,
      layout: {
        blockOrder: form.controls.layout.controls.blockOrder.getRawValue(),
        hiddenBlocks: form.controls.layout.controls.hiddenBlocks.getRawValue(),
      },
      logoArea: {
        ...footer.logoArea,
      },
      paymentArea: {
        ...footer.paymentArea,
      },
      linkArea: {
        ...footer.linkArea,
        isActive: true,
      },
      addressArea: newAddressArea,
      blocks: blocks.map((block, index) => ({
        ...block,
        positionOrder: index + 4,
        links: block.links.map(link => ({
          ...link,
          texts: [...(link.texts ?? []), ...(link.urls ?? [])],
        })),
      })),
    };

    newFooter.linkArea?.links?.forEach((group: any) => {
      const urls: IText[] = group.urls ?? [];
      const texts: IText[] = group.texts ?? [];
      group.texts = [...texts, ...urls];
    });

    this.removeNullIds(newFooter);
    return newFooter as IFooter;
  }

  buildBlockForm(
    block: IFooterBlock,
    positionOrder: number
  ): FormGroup<IFooterBlockForm> {
    return this.formBuilder.group<IFooterBlockForm>({
      id: this.formBuilder.control(block.id ?? null),
      type: this.formBuilder.nonNullable.control(block.type),
      orientation: this.formBuilder.nonNullable.control(
        block.orientation ?? 'vertical'
      ),
      isActive: this.formBuilder.nonNullable.control(block.isActive),
      positionOrder: this.formBuilder.nonNullable.control(
        block.positionOrder ?? positionOrder
      ),
      headlines: this.formBuilder.nonNullable.control(block.headlines ?? []),
      texts: this.formBuilder.nonNullable.control(block.texts ?? []),
      accessibleImages:
        (block.accessibleImages?.length ?? 0) > 0
          ? this.imageFormsFactory.buildFormArray(block.accessibleImages)
          : this.formBuilder.array([
              this.imageFormsFactory.buildImageFormGroup(undefined, 0),
            ]),
      links: this.linkFormsFactory.buildFormArray(block.links ?? []),
    });
  }

  private getLegacyBlocks(footer: IFooter): IFooterBlock[] {
    return [
      {
        ...footer.logoArea,
        id: footer.logoArea.id as unknown as FooterBlockId,
        type: toFooterBlockType(
          footer.logoArea.type,
          DEFAULT_FOOTER_AREA_TYPE.logoArea
        ),
        orientation: (footer.logoArea as any)?.orientation ?? 'vertical',
        positionOrder: 0,
        texts: footer.logoArea.texts ?? [],
        links: footer.logoArea.links ?? [],
      },
      {
        id: footer.addressArea.id as unknown as FooterBlockId,
        type: EFooterBlockType.Text,
        isActive: footer.addressArea.isActive,
        positionOrder: 1,
        headlines: this.filterTexts.transform(
          footer.addressArea.texts,
          ERelationType.headline
        ),
        texts: this.filterTexts.transform(
          footer.addressArea.texts,
          ERelationType.text
        ),
        accessibleImages: [],
        links: [],
      },
      {
        ...footer.paymentArea,
        id: footer.paymentArea.id as unknown as FooterBlockId,
        type: EFooterBlockType.Payments,
        positionOrder: 2,
        texts: [],
        links: [],
      },
      {
        ...footer.linkArea,
        id: footer.linkArea.id as unknown as FooterBlockId,
        type: EFooterBlockType.Links,
        isActive: true,
        positionOrder: 3,
        texts: [],
        accessibleImages: [],
      },
    ];
  }
}
